import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { finalize, map } from 'rxjs';
import { OpcaoRenda } from '../../../../core/enum/parametros/opcao-renda.enum';
import {
  OpcoesRenda,
  TipoValorMinimoRenda,
  UnidadeTempo,
  ValorMinimoRenda,
} from '../../../../core/models/parametros/beneficios.model';
import { BeneficiosService } from '../../../../core/services/parametros/beneficios.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  OPCOES_RENDA_OPCOES,
  OPCOES_RENDA_PADRAO,
  PRAZO_CERTO_MAXIMO,
  UNIDADES_TEMPO_OPCOES,
  TIPOS_VALOR_MINIMO_RENDA_OPCOES,
  descreverOpcoesRenda,
  erroOpcaoRenda,
  valorMinimoRendaOk,
} from '../../../../core/utils/parametros/beneficios.defaults';
import { VivestCheckboxValueAccessorDirective } from '../../../../shared/directives/checkbox-vivest/vivest-checkbox-value-accessor.directive';
import { MascaraMoedaDirective } from '../../../../shared/directives/mascara-moeda/mascara-moeda.directive';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestRadioValueAccessorDirective } from '../../../../shared/directives/radio-vivest/vivest-radio-value-accessor.directive';
import { VivestSwitchValueAccessorDirective } from '../../../../shared/directives/switch-vivest/vivest-switch-value-accessor.directive';
import { ApiHttpError, resolveProblemMessage } from '../../../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../../../shared/services/loader/loader.service';

function algumaOpcao(grupo: AbstractControl): ValidationErrors | null {
  return Object.values(grupo.value as Record<string, boolean>).some(Boolean) ? null : { nenhumaOpcao: true };
}

function valorMinimoValido(grupo: AbstractControl): ValidationErrors | null {
  return valorMinimoRendaOk(grupo.value as ValorMinimoRenda) ? null : { valorMinimoInvalido: true };
}

/** Mensagem da API (400, 404: campo inválido, plano inexistente) ou, sem ela, a mensagem padrão da tela. */
function mensagemDoErro(erro: unknown, padrao: string): string {
  return erro instanceof ApiHttpError && erro.status >= 400 && erro.status < 500 && erro.status !== 401
    ? resolveProblemMessage(erro.problem, padrao)
    : padrao;
}

const checkboxes = (marcadas: readonly OpcaoRenda[] = []) =>
  Object.fromEntries(OPCOES_RENDA_OPCOES.map(({ value }) => [value, marcadas.includes(value)])) as Record<OpcaoRenda, boolean>;

/** Seção "Opções de Renda": as formas de receber a renda e o valor mínimo, para todo o plano. */
@Component({
  selector: 'app-beneficios-opcoes-renda',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MascaraMoedaDirective,
    VivestCheckboxValueAccessorDirective,
    VivestRadioValueAccessorDirective,
    VivestSwitchValueAccessorDirective,
    VivestModalAreaVisivelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './beneficios-opcoes-renda.component.html',
  styleUrl: './beneficios-opcoes-renda.component.scss',
})
export class BeneficiosOpcoesRendaComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly beneficiosService = inject(BeneficiosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly planoId = input.required<number>();
  readonly nomePlano = input('');

  readonly opcoesRenda = OPCOES_RENDA_OPCOES;
  readonly tiposValorMinimo = TIPOS_VALOR_MINIMO_RENDA_OPCOES;
  readonly prazoMaximo = PRAZO_CERTO_MAXIMO;
  readonly unidadesTempo = UNIDADES_TEMPO_OPCOES;
  readonly opcaoRenda = OpcaoRenda;

  /** Último estado salvo, para o Cancelar e para saber se há alterações. */
  private readonly carregado = signal<OpcoesRenda | null>(null);
  readonly carregou = computed(() => this.carregado() !== null);

  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);
  readonly tentouSalvar = signal(false);

  readonly form = this.fb.group({
    opcoes: this.fb.group(checkboxes(), { validators: [algumaOpcao] }),
    /** Valores das opções; só os das marcadas são validados (os outros ficam guardados). */
    parametros: this.fb.group({
      percentualSaldo: this.fb.group({
        minimo: this.fb.control<number | null>(null),
        maximo: this.fb.control<number | null>(null),
      }),
      prazoCerto: this.fb.group({
        minimo: this.fb.control<number | null>(null),
        maximo: this.fb.control<number | null>(null),
        unidade: 'anos' as UnidadeTempo,
      }),
    }),
    valorMinimo: this.fb.group(
      {
        validar: false,
        tipo: 'valor' as TipoValorMinimoRenda,
        valor: this.fb.control<number | null>(null),
        percentual: this.fb.control<number | null>(null),
      },
      { validators: [valorMinimoValido] },
    ),
  });

  readonly valor = toSignal(this.form.valueChanges.pipe(map(() => this.lerFormulario())), {
    initialValue: this.lerFormulario(),
  });

  readonly temAlteracoes = computed(() => {
    const carregado = this.carregado();
    return !!carregado && JSON.stringify(this.valor()) !== JSON.stringify(carregado) && !this.salvando();
  });

  /** Erro de cada opção marcada, pela opção. */
  readonly errosOpcoes = computed(() => {
    const { opcoes, parametros } = this.valor();
    return new Map(opcoes.map((opcao) => [opcao, erroOpcaoRenda(opcao, parametros)] as const).filter(([, erro]) => !!erro));
  });

  readonly invalido = computed(
    () => !this.valor().opcoes.length || this.errosOpcoes().size > 0 || !valorMinimoRendaOk(this.valor().valorMinimo),
  );

  marcada(opcao: OpcaoRenda): boolean {
    return this.valor().opcoes.includes(opcao);
  }

  erroDaOpcao(opcao: OpcaoRenda): string {
    return this.tentouSalvar() ? (this.errosOpcoes().get(opcao) ?? '') : '';
  }

  readonly resumo = computed(() => descreverOpcoesRenda(this.valor()));

  readonly erroOpcoes = computed(() =>
    this.tentouSalvar() && !this.valor().opcoes.length ? 'Marque ao menos uma opção de renda.' : '',
  );

  readonly erroValorMinimo = computed(() => {
    const { valorMinimo } = this.valor();
    if (!this.tentouSalvar() || valorMinimoRendaOk(valorMinimo)) {
      return '';
    }
    return valorMinimo.tipo === 'valor'
      ? 'Informe um valor mínimo maior que R$ 0,00.'
      : 'Informe um percentual de 0,01 a 100, com até duas casas decimais.';
  });

  ngOnInit(): void {
    this.loaderService.showLoader();
    this.beneficiosService
      .obterOpcoesRenda(this.planoId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (opcoes) => {
          this.aplicar(opcoes);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar as opções de renda.', 'error');
        },
      });
  }

  salvar(): void {
    this.tentouSalvar.set(true);
    if (!this.temAlteracoes()) {
      return;
    }
    if (this.invalido()) {
      this.toastService.show('Revise as opções de renda antes de salvar.', 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.invalido() || this.salvando()) {
      return;
    }
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .salvarOpcoesRenda(this.planoId(), this.lerFormulario())
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salvas) => {
          this.aplicar(salvas);
          this.toastService.show('Opções de renda do plano salvas.', 'success');
        },
        error: (erro: unknown) =>
          this.toastService.show(mensagemDoErro(erro, 'Não foi possível salvar as opções de renda.'), 'error'),
      });
  }

  descartar(): void {
    const carregado = this.carregado();
    if (carregado) {
      this.aplicar(carregado);
    }
  }

  private aplicar(opcoes: OpcoesRenda): void {
    const { opcoes: marcadas, parametros, valorMinimo } = { ...OPCOES_RENDA_PADRAO, ...opcoes };
    this.form.reset({ opcoes: checkboxes(marcadas), parametros, valorMinimo });
    this.carregado.set(structuredClone(opcoes));
    this.tentouSalvar.set(false);
  }

  private lerFormulario(): OpcoesRenda {
    const { opcoes, parametros, valorMinimo } = this.form.getRawValue();
    return {
      opcoes: OPCOES_RENDA_OPCOES.map(({ value }) => value).filter((value) => opcoes[value]),
      parametros: {
        ...parametros,
        percentualSaldo: { ...parametros.percentualSaldo },
        prazoCerto: { ...parametros.prazoCerto },
      },
      valorMinimo: { ...valorMinimo },
    };
  }
}
